import React, { useState, useEffect } from 'react';
import { tags } from '../utils/api';
import { toast } from 'react-toastify';
import TagBadge from './Documents/atoms/TagBadge';
import CreateTagModal from './CreateTagModal';

const TagManager = ({ onUpdate }) => {
  const [userTags, setUserTags] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTag, setEditingTag] = useState(null);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const fetchedTags = await tags.getAll();
      setUserTags(fetchedTags);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleTagCreated = async (tag) => {
    await fetchTags();
    if (onUpdate) onUpdate();
    toast.success(editingTag ? 'Tag updated successfully' : 'Tag created successfully');
    setShowCreateModal(false);
    setEditingTag(null);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await tags.delete(tagId);
      await fetchTags();
      if (onUpdate) onUpdate();
      toast.success('Tag deleted successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to delete tag');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create new tag button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Create Tag
        </button>
      </div>

      <CreateTagModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingTag(null);
        }}
        onTagCreated={handleTagCreated}
        initialName={editingTag?.name || ''}
        initialColor={editingTag?.color || ''}
        editMode={!!editingTag}
        tagId={editingTag?.id}
      />

      {/* Tag list */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {userTags.map(tag => (
            <li key={tag.id} className="px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TagBadge 
                    name={tag.name}
                    interactive={false}
                    color={tag.color}
                  />
                  <span className="text-xs text-gray-500">
                    ({tag.coin_count || 0} coin{tag.coin_count !== 1 ? 's' : ''})
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingTag(tag);
                      setShowCreateModal(true);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteTag(tag.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </li>
          ))}
          {userTags.length === 0 && (
            <li className="px-4 py-4 sm:px-6 text-gray-500 text-sm">
              No tags created yet. Create your first tag above.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default TagManager;
